import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CollapsibleAccountInfo, ConfigureProfilePanel, CONFIGURE_PROFILE_VIEW } from '../components/ConfigureProfilePanel';
import { FormSubmitFeedback, SubmitDetailsButton } from '../components/SubmitDetailsButton';
import { FormActionButtons } from '../components/FormActionButtons';
import { SafetyFormHeader } from '../components/FormSiteTimestampControls';
import { HandwrittenSignatureField } from '../components/HandwrittenSignatureField';
import { EcpSilicaForm } from '../components/EcpSilicaForm';
import { FallProtectionPlanForm } from '../components/FallProtectionPlanForm';
import {
  ConfinedSpaceEntryPlanForm,
  HsAcknowledgementForm,
  SwpSignoffForm,
} from '../components/SimpleSignoffForm';
import { LogoutButton } from '../components/LogoutButton';
import { MobileActionBar, useIsDesktop } from '../components/MobileActionBar';
import { FormSubmitProvider } from '../context/FormSubmitContext';
import { UserProfileProvider, useUserProfile } from '../context/UserProfileContext';
import { profileSignatureText, splitProfileSignature } from '../utils/userProfile';
import { apiUrl } from '../config/api';
import { getStoredUser, setStoredUser } from '../utils/auth';
import {
  FORM_FREQUENCY,
  getSafetyFormCardsForRole,
  getSafetyFormCardsByFrequency,
} from '../config/safetyForms';
import { SafetyFormFrequencyNav } from '../components/SafetyFormFrequencyNav';
import { ScrollableRightPane } from '../components/ScrollEdgeButtons';
import { CollapsibleSidebarNav } from '../components/CollapsibleSidebarNav';
import { SubmittedFormsPanel } from '../components/SubmittedFormsPanel';
import { HazardsAndControlPanel } from '../components/HazardsAndControlPanel';
import { SUBMITTED_AND_HAZARDS_SECTION, SUBMITTED_FORMS_VIEW, HAZARDS_CONTROL_VIEW } from '../config/dashboardViews';
import { roleLabel } from '../utils/roles';
import '../styles/userPrintForms.css';

function fillIfEmpty(current, profileValue) {
  return current || profileValue || '';
}

const FORM_ZOOM_MIN = 0.5;
const FORM_ZOOM_MAX = 1.5;
const FORM_ZOOM_STEP = 0.1;

function FormZoomControls({ zoom, onZoomIn, onZoomOut }) {
  const btnClass =
    'h-8 w-8 flex items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-700 text-lg font-medium hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="flex items-center gap-2 shrink-0" aria-label="Form zoom controls">
      <button
        type="button"
        onClick={onZoomOut}
        disabled={zoom <= FORM_ZOOM_MIN}
        className={btnClass}
        title="Zoom out"
        aria-label="Zoom out"
      >
        −
      </button>
      <span className="text-sm font-medium text-gray-600 min-w-[3rem] text-center tabular-nums">
        {Math.round(zoom * 100)}%
      </span>
      <button
        type="button"
        onClick={onZoomIn}
        disabled={zoom >= FORM_ZOOM_MAX}
        className={btnClass}
        title="Zoom in"
        aria-label="Zoom in"
      >
        +
      </button>
    </div>
  );
}

function DesktopLogoutRow({ className = '' }) {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  return (
    <div className={`mb-4 flex items-center justify-end gap-2 ${className}`}>
      <LogoutButton />
    </div>
  );
}

function DesktopFormActions() {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  return <FormActionButtons className="mb-2 shrink-0" />;
}

function UserSafetyDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || getStoredUser());
  const companyName = user?.company_name || user?.project_name || 'HINDBO';
  const companyLogoUrl = user?.company_logo_url ? apiUrl(user.company_logo_url) : null;

  useEffect(() => {
    const email = user?.email;
    if (!email) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/users/me'), {
          headers: { 'X-User-Email': email },
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.user) {
          setUser(data.user);
          setStoredUser(data.user);
        }
      } catch {
        /* keep cached session */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);
  const displayRoleLabel = user?.role ? roleLabel(user.role) : null;
  const formRef = useRef(null);

  const formCards = useMemo(
    () => getSafetyFormCardsForRole(user?.role || 'user', companyName),
    [companyName, user?.role]
  );
  const [formFrequency, setFormFrequency] = useState(FORM_FREQUENCY.YEARLY);
  const [formStatus, setFormStatus] = useState('all');
  const [formFilterKind, setFormFilterKind] = useState('status'); // 'status' | 'frequency'

  const filteredFormCards = useMemo(() => {
    if (formFilterKind === 'status') {
      if (formStatus === 'overdue') return [];
      return formCards;
    }
    return getSafetyFormCardsByFrequency(companyName, formFrequency, user?.role || 'user');
  }, [companyName, formCards, formFrequency, formStatus, formFilterKind, user?.role]);

  const [activeIndex, setActiveIndex] = useState(() => formCards[0]?.index ?? 12);
  const [activeView, setActiveView] = useState('forms');
  const [formZoom, setFormZoom] = useState(1);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!formCards.some((card) => card.index === activeIndex)) {
      setActiveIndex(formCards[0]?.index ?? 12);
    }
  }, [formCards, activeIndex]);

  const handleZoomIn = () =>
    setFormZoom((z) => Math.min(FORM_ZOOM_MAX, +(z + FORM_ZOOM_STEP).toFixed(2)));
  const handleZoomOut = () =>
    setFormZoom((z) => Math.max(FORM_ZOOM_MIN, +(z - FORM_ZOOM_STEP).toFixed(2)));

  const handleFormStatusChange = (nextStatus) => {
    setFormFilterKind('status');
    setFormStatus(nextStatus);
    setActiveView('forms');
  };

  const handleFrequencyChange = (nextFrequency) => {
    setFormFilterKind('frequency');
    setFormFrequency(nextFrequency);
    const nextCards = getSafetyFormCardsByFrequency(
      companyName,
      nextFrequency,
      user?.role || 'user'
    );
    setActiveView('forms');
    if (!nextCards.some((card) => card.index === activeIndex)) {
      setActiveIndex(nextCards[0]?.index ?? formCards[0]?.index ?? 12);
    }
  };

  const handleSelectForm = (card) => {
    setActiveView('forms');
    setActiveIndex(card.index);
    setMobileNavOpen(false);
  };

  const handleBackHome = () => {
    navigate('/');
  };

  const activeFormTitle =
    formCards.find((card) => card.index === activeIndex)?.title || 'Safety Form';

  const statusEmptyText =
    formFilterKind === 'status' && formStatus === 'overdue'
      ? 'No overdue forms yet.'
      : formFilterKind === 'frequency' && formFrequency === FORM_FREQUENCY.YEARLY
        ? 'No yearly forms in this list.'
        : formFilterKind === 'status' && formStatus === 'all'
          ? 'No forms found.'
          : 'No forms in this category.';

  return (
    <UserProfileProvider email={user?.email}>
    <div className="relative h-screen bg-black text-white flex overflow-hidden">
      {activeView === 'forms' ? null : (
        <MobileActionBar menuOpen={mobileNavOpen} onOpenMenu={() => setMobileNavOpen(true)}>
          <LogoutButton className="inline-flex h-9 items-center px-2.5 py-0 text-xs" />
        </MobileActionBar>
      )}
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}
      {/* Left sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[min(20rem,calc(100vw-2rem))] bg-gray-950 border-r border-gray-800 p-4 flex flex-col overflow-hidden transition-transform duration-200 lg:static lg:z-auto lg:w-80 lg:translate-x-0 lg:p-6 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-700 text-xl text-gray-300 hover:text-white lg:hidden"
          aria-label="Close navigation"
        >
          ×
        </button>
        {companyLogoUrl ? (
          <div className="mb-6 w-full h-20 rounded-md border border-gray-700 bg-gray-900 overflow-hidden flex items-center justify-center shrink-0">
            <img
              src={companyLogoUrl}
              alt={`${companyName} logo`}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <div className="mb-6 w-full h-20 rounded-md border border-dashed border-gray-600 flex items-center justify-center text-sm text-gray-500 bg-gray-900 shrink-0">
            Company logo
          </div>
        )}
        <div className="mb-6 shrink-0">
          <h1 className="text-2xl font-bold truncate">{companyName}</h1>
          {user ? (
            <CollapsibleAccountInfo
              email={user.email}
              emailPrefix="Logged in as "
              roleLabel={displayRoleLabel}
              roleClassName="text-amber-200/90"
              profileActive={activeView === CONFIGURE_PROFILE_VIEW || activeView === 'profile'}
              onConfigureProfile={() => {
                setActiveView('profile');
                setMobileNavOpen(false);
              }}
            />
          ) : null}
        </div>
        <CollapsibleSidebarNav
          label={SUBMITTED_AND_HAZARDS_SECTION.label}
          cards={SUBMITTED_AND_HAZARDS_SECTION.cards}
          activeView={activeView}
          onSelect={(view) => {
            setActiveView(view);
            setMobileNavOpen(false);
          }}
        />
        <SafetyFormFrequencyNav
          cards={filteredFormCards}
          frequency={formFrequency}
          onFrequencyChange={handleFrequencyChange}
          activeFormIndex={activeIndex}
          onSelectForm={handleSelectForm}
          showFormStatusTabs
          formStatus={formStatus}
          onFormStatusChange={handleFormStatusChange}
          showFrequencyTabs
          activeFilterKind={formFilterKind}
          emptyText={statusEmptyText}
        />
        <button
          type="button"
          onClick={handleBackHome}
          className="mt-6 text-xs text-gray-400 hover:text-white underline shrink-0"
        >
          Back to Home
        </button>
      </aside>

      {/* Right content area */}
      <main
        className={`flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden px-2 pb-3 pt-16 sm:px-4 lg:px-8 lg:pb-8 ${
          activeView === 'profile' ? 'lg:pt-8' : 'lg:pt-3'
        }`}
      >
        {activeView === 'profile' ? (
          <ScrollableRightPane
            className="max-w-5xl mx-auto w-full flex-1 rounded-lg border border-gray-800 bg-gray-950/70"
            scrollClassName="p-3 sm:p-4 lg:p-8"
          >
            <DesktopLogoutRow />
            <ConfigureProfilePanel onBack={() => setActiveView('forms')} backLabel="← Back to forms" />
          </ScrollableRightPane>
        ) : activeView === SUBMITTED_FORMS_VIEW || activeView === HAZARDS_CONTROL_VIEW ? (
          <ScrollableRightPane
            className="max-w-5xl mx-auto w-full flex-1 rounded-lg border border-gray-800 bg-gray-950/70"
            scrollClassName="p-3 sm:p-4 lg:p-8"
          >
            <DesktopLogoutRow />
            {activeView === SUBMITTED_FORMS_VIEW ? (
              <SubmittedFormsPanel onBack={() => setActiveView('forms')} ownOnly />
            ) : (
              <HazardsAndControlPanel onBack={() => setActiveView('forms')} />
            )}
          </ScrollableRightPane>
        ) : (
          <div className="max-w-5xl mx-auto w-full flex flex-col flex-1 min-h-0">
            <FormSubmitProvider
              formRef={formRef}
              formIndex={activeIndex}
              formTitle={activeFormTitle}
              userEmail={user?.email}
              companyLogoUrl={companyLogoUrl}
            >
              <MobileActionBar menuOpen={mobileNavOpen} onOpenMenu={() => setMobileNavOpen(true)}>
                <FormActionButtons className="gap-1.5" />
              </MobileActionBar>
              <DesktopFormActions />
              <div className="flex flex-col flex-1 min-h-0 rounded-lg border border-gray-300 bg-white shadow-sm overflow-hidden">
                <SafetyFormHeader
                  title={activeFormTitle}
                  companyLogoUrl={companyLogoUrl}
                  companyName={companyName}
                  rightAside={
                    <FormZoomControls
                      zoom={formZoom}
                      onZoomIn={handleZoomIn}
                      onZoomOut={handleZoomOut}
                    />
                  }
                />
                <ScrollableRightPane
                  className="flex-1"
                  scrollClassName="px-3 sm:px-4 lg:px-8 py-4"
                  tone="light"
                >
                  <div
                    ref={formRef}
                    className="user-print-form"
                    style={{ zoom: formZoom }}
                  >
                    <SafetyFormContent formIndex={activeIndex} projectName={companyName} />
                  </div>
                </ScrollableRightPane>
                <div className="shrink-0 border-t border-gray-300 bg-white px-3 sm:px-4 lg:px-8 py-2">
                  <SubmitDetailsButton />
                </div>
                <FormSubmitFeedback />
              </div>
            </FormSubmitProvider>
          </div>
        )}
      </main>
    </div>
    </UserProfileProvider>
  );
}

const FIRE_EXTINGUISHER_MAX_MONTHS = 12;

function createFireExtinguisherEntry(profileName = '') {
  return {
    location: '',
    extinguisherType: '',
    serialOrAsset: '',
    month: '',
    inspectionDate: '',
    inspectedBy: profileName,
    pressureOk: '',
    pinSealOk: '',
    signageOk: '',
    accessOk: '',
    conditionOk: '',
    comments: '',
  };
}

function isFireExtinguisherEntryEmpty(entry, profileName = '') {
  const filledKeys = [
    'location',
    'extinguisherType',
    'serialOrAsset',
    'month',
    'inspectionDate',
    'pressureOk',
    'pinSealOk',
    'signageOk',
    'accessOk',
    'conditionOk',
    'comments',
  ];
  if (filledKeys.some((key) => String(entry?.[key] || '').trim())) return false;
  const inspectedBy = String(entry?.inspectedBy || '').trim();
  const autoName = String(profileName || '').trim();
  return !inspectedBy || inspectedBy === autoName;
}

function FireExtinguisherInspectionLogForm() {
  // TODO: Replace fields/labels with exact PDF once available in repo:
  // "Fire Extinguisher Inspection Log.pdf"
  const { name: profileName } = useUserProfile();
  const [meta, setMeta] = useState({
    siteOrProject: '',
    month: '',
    year: '',
    supervisor: profileName,
  });

  const [entries, setEntries] = useState(() => [createFireExtinguisherEntry(profileName)]);

  const setEntry = (index, patch) => {
    setEntries((prev) => {
      if (index !== prev.length - 1) return prev;
      return prev.map((row, i) => (i === index ? { ...row, ...patch } : row));
    });
  };

  const addNextMonth = () => {
    setEntries((prev) => {
      if (prev.length >= FIRE_EXTINGUISHER_MAX_MONTHS) return prev;
      return [...prev, createFireExtinguisherEntry('')];
    });
  };

  const removeEntry = (index) => {
    setEntries((prev) => {
      if (!isFireExtinguisherEntryEmpty(prev[index], profileName)) return prev;
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [createFireExtinguisherEntry(profileName)];
    });
  };

  useEffect(() => {
    if (!profileName) return;
    setMeta((p) => ({ ...p, supervisor: fillIfEmpty(p.supervisor, profileName) }));
    setEntries((prev) =>
      prev.map((row, index) =>
        index === prev.length - 1
          ? { ...row, inspectedBy: fillIfEmpty(row.inspectedBy, profileName) }
          : row
      )
    );
  }, [profileName]);

  const fieldClass = (frozen) =>
    `w-full px-2 py-1 rounded border text-xs md:text-sm focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent ${
      frozen
        ? 'bg-gray-900 border-gray-800 text-gray-400 cursor-not-allowed'
        : 'bg-black border-gray-700 text-white placeholder-gray-500'
    }`;

  return (
    <form className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <div className="flex flex-col md:col-span-2">
          <label className="text-xs font-medium text-gray-300 mb-1">Site / Project</label>
          <input
            type="text"
            value={meta.siteOrProject}
            onChange={(e) => setMeta((p) => ({ ...p, siteOrProject: e.target.value }))}
            placeholder="Enter site / project"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Month</label>
          <input
            type="text"
            value={meta.month}
            onChange={(e) => setMeta((p) => ({ ...p, month: e.target.value }))}
            placeholder="Month"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Year</label>
          <input
            type="text"
            value={meta.year}
            onChange={(e) => setMeta((p) => ({ ...p, year: e.target.value }))}
            placeholder="Year"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Supervisor</label>
          <input
            type="text"
            value={meta.supervisor}
            onChange={(e) => setMeta((p) => ({ ...p, supervisor: e.target.value }))}
            placeholder="Supervisor name"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-800 rounded-lg">
        <table className="min-w-full text-xs md:text-sm">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Month</th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Location</th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Type</th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Serial / Asset</th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Inspection Date</th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Inspected By</th>
              <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Pressure</th>
              <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Pin/Seal</th>
              <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Signage</th>
              <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Access</th>
              <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Condition</th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">Comments</th>
              <th className="px-3 py-2 text-right font-medium text-gray-300 w-16"> </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => {
              const frozen = index < entries.length - 1;
              const canDelete =
                entries.length > 1 && isFireExtinguisherEntryEmpty(entry, profileName);
              return (
                <tr
                  key={index}
                  className={`${index % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'} ${
                    frozen ? 'opacity-70' : ''
                  }`}
                >
                  <td className="px-3 py-2 align-top">
                    <input
                      type="month"
                      value={entry.month}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { month: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={entry.location}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { location: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={entry.extinguisherType}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { extinguisherType: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={entry.serialOrAsset}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { serialOrAsset: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="date"
                      value={entry.inspectionDate}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { inspectionDate: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={entry.inspectedBy}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { inspectedBy: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  {[
                    ['pressureOk', 'Pressure'],
                    ['pinSealOk', 'Pin/Seal'],
                    ['signageOk', 'Signage'],
                    ['accessOk', 'Access'],
                    ['conditionOk', 'Condition'],
                  ].map(([key]) => (
                    <td key={`${index}-${key}`} className="px-3 py-2 align-top">
                      <div className="flex justify-center">
                        <select
                          value={entry[key]}
                          disabled={frozen}
                          onChange={(e) => setEntry(index, { [key]: e.target.value })}
                          className={fieldClass(frozen)}
                        >
                          <option value="">—</option>
                          <option value="ok">OK</option>
                          <option value="not_ok">Not OK</option>
                          <option value="na">N/A</option>
                        </select>
                      </div>
                    </td>
                  ))}
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={entry.comments}
                      disabled={frozen}
                      onChange={(e) => setEntry(index, { comments: e.target.value })}
                      className={fieldClass(frozen)}
                    />
                  </td>
                  <td className="px-2 py-2 align-top text-right">
                    {canDelete ? (
                      <button
                        type="button"
                        onClick={() => removeEntry(index)}
                        className="inline-flex h-8 w-8 items-center justify-center text-lg leading-none text-red-400 hover:text-red-300"
                        title="Delete empty row"
                        aria-label="Delete empty row"
                      >
                        ×
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={addNextMonth}
          disabled={entries.length >= FIRE_EXTINGUISHER_MAX_MONTHS}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-950 text-xl font-semibold leading-none text-blue-900/80 hover:bg-blue-950/50 disabled:cursor-not-allowed disabled:opacity-40"
          title="Add next month"
          aria-label="Add next month"
        >
          +
        </button>
        <p className="text-xs text-gray-500">Add next month</p>
      </div>
    </form>
  );
}

function ExcavationInspectionForm({ projectName }) {
  const { name: profileName } = useUserProfile();
  // TODO: Replace the sections/items below with the exact checklist items from
  // "Excavation Inspection HINDBO.pdf" once the PDF is available in this repo.
  const sections = [
    {
      title: 'Pre-Excavation Planning',
      items: [
        'Utility locates completed and available on site',
        'Excavation plan reviewed with crew (scope, depth, access, egress)',
        'Traffic control in place where required',
      ],
    },
    {
      title: 'Protective Systems',
      items: [
        'Protective system selected (sloping/benching/shoring/shielding)',
        'Shoring installed per manufacturer / engineered requirements',
        'Trench box/shield in good condition and installed correctly',
        'Spoil/materials/equipment kept back from edge',
      ],
    },
    {
      title: 'Access & Egress',
      items: [
        'Safe access/egress provided (ladder/ramp) and secured',
      ],
    },
    {
      title: 'Hazard Controls',
      items: [
        'Barricades / fencing / signage in place around excavation',
        'Soil conditions assessed and recorded (type, stability, water)',
        'Walls inspected for sloughing, cracking, or instability',
        'Water accumulation controlled (pumps, drainage, diversion)',
        'No personnel working under suspended loads',
      ],
    },
    {
      title: 'Worker Safety',
      items: [
        'Required PPE worn (hard hat, boots, hi-vis, eye/hand protection)',
        'Atmospheric hazards assessed where required',
        'Housekeeping adequate (trip hazards controlled)',
        'Excavator/loader pre-use checks completed',
        'Operators certified/authorized',
        'Spotter used when required and communication is clear',
        'Swing radius and exclusion zones maintained',
      ],
    },
    {
      title: 'Documentation',
      items: [
        // Keep as placeholder until the PDF is provided.
        'Records / notes completed and retained as required',
      ],
    },
  ];

  const [meta, setMeta] = useState({
    projectNameAndLocation: projectName ? `${projectName}` : '',
    dateAndTimeOfInspection: '',
    inspectorNameAndTitle: profileName,
    weatherConditions: '',
    typeOfExcavation: '',
    depthAndDimensionsOfExcavation: '',
  });

  const [responses, setResponses] = useState(() => {
    const initial = {};
    sections.forEach((section) => {
      section.items.forEach((item) => {
        initial[`${section.title}::${item}`] = { value: '', comment: '' };
      });
    });
    return initial;
  });

  useEffect(() => {
    if (!profileName) return;
    setMeta((p) => ({
      ...p,
      inspectorNameAndTitle: fillIfEmpty(p.inspectorNameAndTitle, profileName),
    }));
  }, [profileName]);

  const setResponseValue = (key, value) => {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { value: '', comment: '' }), value },
    }));
  };

  const setResponseComment = (key, comment) => {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { value: '', comment: '' }), comment },
    }));
  };

  let globalIndex = 0;

  return (
    <form className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-4">General Site Information</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">
              Project name &amp; location
            </label>
            <input
              type="text"
              value={meta.projectNameAndLocation}
              onChange={(e) => setMeta((p) => ({ ...p, projectNameAndLocation: e.target.value }))}
              placeholder="Project name & location"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">
              Date &amp; time of inspection
            </label>
            <input
              type="datetime-local"
              value={meta.dateAndTimeOfInspection}
              onChange={(e) => setMeta((p) => ({ ...p, dateAndTimeOfInspection: e.target.value }))}
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">
              Inspector name &amp; title
            </label>
            <input
              type="text"
              value={meta.inspectorNameAndTitle}
              onChange={(e) => setMeta((p) => ({ ...p, inspectorNameAndTitle: e.target.value }))}
              placeholder="Inspector name & title"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Weather conditions</label>
            <input
              type="text"
              value={meta.weatherConditions}
              onChange={(e) => setMeta((p) => ({ ...p, weatherConditions: e.target.value }))}
              placeholder="Weather conditions"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">
              Type of excavation (trench, pit, etc)
            </label>
            <input
              type="text"
              value={meta.typeOfExcavation}
              onChange={(e) => setMeta((p) => ({ ...p, typeOfExcavation: e.target.value }))}
              placeholder="Type of excavation (trench, pit, etc)"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">
              Depth &amp; dimensions of excavation
            </label>
            <input
              type="text"
              value={meta.depthAndDimensionsOfExcavation}
              onChange={(e) =>
                setMeta((p) => ({ ...p, depthAndDimensionsOfExcavation: e.target.value }))
              }
              placeholder="Depth & dimensions of excavation"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {sections
        .filter((s) => s.items.length > 0)
        .map((section) => (
          <div key={section.title}>
            <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
            <div className="overflow-x-auto border border-gray-800 rounded-lg">
              <table className="min-w-full text-xs md:text-sm table-fixed">
                <colgroup>
                  <col className="w-1/2" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-1/3" />
                </colgroup>
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-300">Item</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Yes</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">No</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">N/A</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-300">Notes / Corrective Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((label) => {
                    const rowIndex = globalIndex;
                    globalIndex += 1;
                    const key = `${section.title}::${label}`;
                    const selected = responses[key]?.value || '';

                    return (
                      <tr key={key} className={rowIndex % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}>
                        <td className="px-3 py-2 align-top text-gray-200">
                          {rowIndex + 1}. {label}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex justify-center">
                            <input
                              type="radio"
                              name={`exc-item-${rowIndex}`}
                              checked={selected === 'yes'}
                              onChange={() => setResponseValue(key, 'yes')}
                              className="h-5 w-5 text-emerald-400 bg-black border-gray-700"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex justify-center">
                            <input
                              type="radio"
                              name={`exc-item-${rowIndex}`}
                              checked={selected === 'no'}
                              onChange={() => setResponseValue(key, 'no')}
                              className="h-5 w-5 text-red-400 bg-black border-gray-700"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex justify-center">
                            <input
                              type="radio"
                              name={`exc-item-${rowIndex}`}
                              checked={selected === 'na'}
                              onChange={() => setResponseValue(key, 'na')}
                              className="h-5 w-5 text-gray-400 bg-black border-gray-700"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <input
                            type="text"
                            value={responses[key]?.comment || ''}
                            onChange={(e) => setResponseComment(key, e.target.value)}
                            placeholder="Enter text here"
                            className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

    </form>
  );
}

function OfficeShopInspectionForm() {
  const { name: profileName } = useUserProfile();
  const [inspector, setInspector] = useState(profileName);
  const [inspectionDate, setInspectionDate] = useState('');

  useEffect(() => {
    if (!profileName) return;
    setInspector((current) => fillIfEmpty(current, profileName));
  }, [profileName]);

  const sections = [
    {
      title: 'Bulletin Boards & Signs',
      items: [
        'Are they clean & readable?',
        'Do they avoid interfering with people passing by?',
      ],
    },
    {
      title: 'Floors',
      items: [
        'Are floors in good condition without holes, loose tiles, or other hazards?',
        'Are there debris, loose material, tripping hazards, slippery or wet?',
        'Is shop floor clear of debris? Wood stacked safely? Cords coiled & away?',
      ],
    },
    {
      title: 'Stairways & Aisles',
      items: [
        'Are they clear & unblocked?',
        'Are they well lit?',
      ],
    },
    {
      title: 'Equipment',
      items: [
        'Are any guards or screens in place and effective?',
        'Is the furniture in good repair & safe to use?',
        'Ergonomics acceptable? (keyboard elevation, chair/desk height)',
        'Are extension cords checked for cuts & frays? Coiled/put away after use?',
        'Are file cabinets heavily loaded? Drawers closed?',
      ],
    },
    {
      title: 'Emergency Equipment',
      items: [
        'Is emergency equipment in place and clearly identified?',
        'Are emergency procedures in place? (Evac, fire, bomb, violence)?',
        'Is emergency lighting in place & regularly tested?',
        'Is all fire control equipment regularly tested & certified?',
      ],
    },
    {
      title: 'Whole Building',
      items: [
        'Are all exits clear & unblocked? (shop + office)',
        'Are passage ways clear of obstructions?',
        'Entry/exits provide personal safety for workers at night?',
        'Are there any hazardous products? If yes: SDS? Labelled? Workers trained?',
        'Are materials stored safely?',
        'Is the air handling system free of dust and fumes?',
        'Is humidity within optimal range? (30-45%)',
        'Are washrooms & lunch room clean with adequate supplies?',
        'Is lighting adequate for the work being done? (clean covers/bulbs)?',
        'Are the shop shelves neat & not overweight with inventory?',
        'Waste baskets stored so not tripped upon?',
        'Are first aid kits resupplied & ready for use?',
        'Do employees know how to get first aid assistance when needed?',
      ],
    },
  ];

  let indexCounter = 0;

  return (
    <form className="space-y-8">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Inspector</label>
          <input
            type="text"
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            placeholder="Inspector name"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Date</label>
          <input
            type="date"
            value={inspectionDate}
            onChange={(e) => setInspectionDate(e.target.value)}
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
      </div>

      {sections.map((section) => (
        <div key={section.title}>
          <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
          <div className="overflow-x-auto border border-gray-800 rounded-lg">
            <table className="min-w-full text-xs md:text-sm table-fixed">
              <colgroup>
                <col className="w-1/2" />
                <col className="w-16" />
                <col className="w-16" />
                <col className="w-16" />
                <col className="w-1/3" />
              </colgroup>
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Location / Condition</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Yes</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">No</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">N/A</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Comment</th>
                </tr>
              </thead>
              <tbody>
                {section.items.map((label) => {
                  const rowIndex = indexCounter;
                  indexCounter += 1;
                  return (
                    <tr
                      key={label}
                      className={rowIndex % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}
                    >
                      <td className="px-3 py-2 align-top text-gray-200">
                        {rowIndex + 1}. {label}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex justify-center">
                          <input
                            type="radio"
                            name={`office-item-${rowIndex}`}
                            className="h-5 w-5 text-emerald-400 bg-black border-gray-700"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex justify-center">
                          <input
                            type="radio"
                            name={`office-item-${rowIndex}`}
                            className="h-5 w-5 text-red-400 bg-black border-gray-700"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex justify-center">
                          <input
                            type="radio"
                            name={`office-item-${rowIndex}`}
                            className="h-5 w-5 text-gray-400 bg-black border-gray-700"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="text"
                          placeholder="Enter text here"
                          className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

    </form>
  );
}

function PreJobHazardAssessmentForm({ projectName }) {
  const { name: profileName, signature: rawProfileSignature } = useUserProfile();
  const profileSig = splitProfileSignature(rawProfileSignature);
  const [projectInfo, setProjectInfo] = useState({
    company: '',
    projectNameLocation: projectName || '',
    date: '',
    time: '',
    supervisorForeman: profileName,
    crewMembersPresent: '',
    signOffNamePrint: profileName,
    signOffSignature: profileSig.text,
    signOffSignatureImage: profileSig.image,
    signOffDate: '',
    supervisorAuthName: profileName,
    supervisorAuthSignature: profileSig.text,
    supervisorAuthSignatureImage: profileSig.image,
    supervisorAuthDate: '',
  });

  useEffect(() => {
    if (!profileName && !rawProfileSignature) return;
    const split = splitProfileSignature(rawProfileSignature);
    setProjectInfo((p) => ({
      ...p,
      supervisorForeman: fillIfEmpty(p.supervisorForeman, profileName),
      signOffNamePrint: fillIfEmpty(p.signOffNamePrint, profileName),
      signOffSignature: fillIfEmpty(p.signOffSignature, split.text),
      signOffSignatureImage: fillIfEmpty(p.signOffSignatureImage, split.image),
      supervisorAuthName: fillIfEmpty(p.supervisorAuthName, profileName),
      supervisorAuthSignature: fillIfEmpty(p.supervisorAuthSignature, split.text),
      supervisorAuthSignatureImage: fillIfEmpty(p.supervisorAuthSignatureImage, split.image),
    }));
  }, [profileName, rawProfileSignature]);

  const [taskDescription, setTaskDescription] = useState('');

  const [taskHazardRows, setTaskHazardRows] = useState(() => [
    { taskStep: '', potentialHazards: '' },
    { taskStep: '', potentialHazards: '' },
    { taskStep: '', potentialHazards: '' },
  ]);

  const [hazardRiskRows, setHazardRiskRows] = useState(() => [
    { taskStep: '', hazard: '', likelihood: '', severity: '' },
    { taskStep: '', hazard: '', likelihood: '', severity: '' },
    { taskStep: '', hazard: '', likelihood: '', severity: '' },
  ]);

  const [controlsRows, setControlsRows] = useState(() => [
    { hazard: '', existingControls: '', additionalControlsRequired: '', controlType: '' },
    { hazard: '', existingControls: '', additionalControlsRequired: '', controlType: '' },
    { hazard: '', existingControls: '', additionalControlsRequired: '', controlType: '' },
  ]);

  const calcRiskScore = (likelihood, severity) => {
    const l = Number(likelihood);
    const s = Number(severity);
    if (!Number.isFinite(l) || !Number.isFinite(s)) return '';
    return String(l * s);
  };

  const getRiskLevel = (riskScore) => {
    const score = Number(riskScore);
    if (!Number.isFinite(score)) return '';
    if (score <= 4) return 'Low';
    if (score <= 9) return 'Moderate';
    if (score <= 16) return 'High';
    return 'Extreme';
  };

  const getRiskAction = (riskScore) => {
    const score = Number(riskScore);
    if (!Number.isFinite(score)) return '';
    if (score <= 4) return 'Manage with routine procedures';
    if (score <= 9) return 'Apply additional controls; monitor';
    if (score <= 16) return 'Stop work until controls in place; supervisor approval required';
    return 'Stop work; senior management/prime contractor review before proceeding';
  };

  const updateTaskHazardRow = (idx, patch) => {
    setTaskHazardRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const updateHazardRiskRow = (idx, patch) => {
    setHazardRiskRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const updateControlsRow = (idx, patch) => {
    setControlsRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const canRemoveTaskHazardRow = taskHazardRows.length > 1;
  const removeLastTaskHazardRow = () => {
    setTaskHazardRows((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  };

  const canRemoveHazardRiskRow = hazardRiskRows.length > 1;
  const removeLastHazardRiskRow = () => {
    setHazardRiskRows((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  };

  const canRemoveControlsRow = controlsRows.length > 1;
  const removeLastControlsRow = () => {
    setControlsRows((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  };

  return (
    <form className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-4">Project / Job Information</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Company</label>
            <input
              type="text"
              value={projectInfo.company}
              onChange={(e) => setProjectInfo((p) => ({ ...p, company: e.target.value }))}
              placeholder="Company"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Project Name / Location</label>
            <input
              type="text"
              value={projectInfo.projectNameLocation}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, projectNameLocation: e.target.value }))
              }
              placeholder="Project Name / Location"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Date</label>
            <input
              type="date"
              value={projectInfo.date}
              onChange={(e) => setProjectInfo((p) => ({ ...p, date: e.target.value }))}
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Time</label>
            <input
              type="time"
              value={projectInfo.time}
              onChange={(e) => setProjectInfo((p) => ({ ...p, time: e.target.value }))}
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Supervisor / Foreman</label>
            <input
              type="text"
              value={projectInfo.supervisorForeman}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, supervisorForeman: e.target.value }))
              }
              placeholder="Supervisor / Foreman"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Crew Members Present</label>
            <input
              type="text"
              value={projectInfo.crewMembersPresent}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, crewMembersPresent: e.target.value }))
              }
              placeholder="Names / count"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Scope of Work / Task Description</h3>
        <textarea
          value={taskDescription}
          onChange={(e) => setTaskDescription(e.target.value)}
          placeholder="Describe the job or task to be performed, including tools, equipment, and materials..."
          className="w-full min-h-28 px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Step 1 - Identify Hazards</h3>
        <p className="text-xs text-gray-400 mb-3">
          List each task step and associated hazards (physical, chemical, biological, ergonomic, environmental).
        </p>
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="min-w-full text-xs md:text-sm table-fixed">
            <colgroup>
              <col className="w-1/3" />
              <col className="w-2/3" />
            </colgroup>
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Task Step</th>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Potential Hazards</th>
              </tr>
            </thead>
            <tbody>
              {taskHazardRows.map((row, idx) => (
                <tr key={`task-hazard-${idx}`} className={idx % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={row.taskStep}
                      onChange={(e) => updateTaskHazardRow(idx, { taskStep: e.target.value })}
                      placeholder={`Task step ${idx + 1}`}
                      className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={row.potentialHazards}
                      onChange={(e) => updateTaskHazardRow(idx, { potentialHazards: e.target.value })}
                      placeholder="Describe hazards..."
                      className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={() => setTaskHazardRows((prev) => [...prev, { taskStep: '', potentialHazards: '' }])}
            className="px-4 py-2 rounded bg-gray-800 text-gray-100 text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            + Add Task Step
          </button>
        </div>
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={removeLastTaskHazardRow}
            disabled={!canRemoveTaskHazardRow}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              canRemoveTaskHazardRow
                ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                : 'bg-gray-900 text-gray-500 cursor-not-allowed'
            }`}
          >
            Remove Last Task Step
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Step 2 - Risk Assessment</h3>

        <div className="mb-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="border border-gray-800 rounded-lg p-3 bg-gray-950/30">
              <div className="text-xs font-medium text-gray-300 mb-2">Likelihood (L)</div>
              <div className="text-xs text-gray-400">1 - Rare</div>
              <div className="text-xs text-gray-400">2 - Unlikely</div>
              <div className="text-xs text-gray-400">3 - Possible</div>
              <div className="text-xs text-gray-400">4 - Likely</div>
              <div className="text-xs text-gray-400">5 - Almost Certain</div>
            </div>
            <div className="border border-gray-800 rounded-lg p-3 bg-gray-950/30">
              <div className="text-xs font-medium text-gray-300 mb-2">Severity (S)</div>
              <div className="text-xs text-gray-400">1 - Insignificant (first aid only)</div>
              <div className="text-xs text-gray-400">2 - Minor (medical aid, no lost time)</div>
              <div className="text-xs text-gray-400">3 - Moderate (lost time injury)</div>
              <div className="text-xs text-gray-400">4 - Major (permanent disability)</div>
              <div className="text-xs text-gray-400">5 - Catastrophic (fatality)</div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="min-w-full text-xs md:text-sm table-fixed">
            <colgroup>
              <col className="w-1/3" />
              <col className="w-1/3" />
              <col className="w-14" />
              <col className="w-14" />
              <col className="w-24" />
              <col className="w-28" />
            </colgroup>
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Task Step</th>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Hazard</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">L</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">S</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-24">Risk Score</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-28">Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {hazardRiskRows.map((row, idx) => {
                const score = calcRiskScore(row.likelihood, row.severity);
                const level = getRiskLevel(score);
                return (
                  <tr
                    key={`hazard-risk-${idx}`}
                    className={idx % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}
                  >
                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        value={row.taskStep}
                        onChange={(e) => updateHazardRiskRow(idx, { taskStep: e.target.value })}
                        placeholder={`Task step ${idx + 1}`}
                        className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        value={row.hazard}
                        onChange={(e) => updateHazardRiskRow(idx, { hazard: e.target.value })}
                        placeholder="Hazard"
                        className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <select
                        value={row.likelihood}
                        onChange={(e) => updateHazardRiskRow(idx, { likelihood: e.target.value })}
                        className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                      >
                        <option value="">—</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <select
                        value={row.severity}
                        onChange={(e) => updateHazardRiskRow(idx, { severity: e.target.value })}
                        className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                      >
                        <option value="">—</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        readOnly
                        value={score}
                        className="w-full h-9 px-2 rounded bg-gray-950 border border-gray-800 text-xs md:text-sm text-gray-100 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        readOnly
                        value={level}
                        className="w-full h-9 px-2 rounded bg-gray-950 border border-gray-800 text-xs md:text-sm text-gray-100 focus:outline-none"
                      />
                      {score ? (
                        <div className="text-[10px] text-gray-500 mt-1 leading-tight break-words whitespace-normal">
                          {getRiskAction(score)}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={() =>
              setHazardRiskRows((prev) => [
                ...prev,
                { taskStep: '', hazard: '', likelihood: '', severity: '' },
              ])
            }
            className="px-4 py-2 rounded bg-gray-800 text-gray-100 text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            + Add Hazard & Risk Row
          </button>
        </div>
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={removeLastHazardRiskRow}
            disabled={!canRemoveHazardRiskRow}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              canRemoveHazardRiskRow
                ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                : 'bg-gray-900 text-gray-500 cursor-not-allowed'
            }`}
          >
            Remove Last Hazard & Risk Row
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Step 3 - Apply Controls (Hierarchy of Controls)</h3>
        <p className="text-xs text-gray-400 mb-3">
          Controls must follow the hierarchy: Elimination → Substitution → Engineering → Administrative → PPE
        </p>

        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="min-w-full text-xs md:text-sm table-fixed">
            <colgroup>
              <col className="w-1/4" />
              <col className="w-2/4" />
              <col className="w-2/5" />
              <col className="w-40" />
            </colgroup>
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Hazard</th>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Existing Controls</th>
                <th className="px-3 py-2 text-left font-medium text-gray-300">
                  Additional Controls Required
                </th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-40">
                  Control Type (E, S, Eng, A, PPE)
                </th>
              </tr>
            </thead>
            <tbody>
              {controlsRows.map((row, idx) => (
                <tr
                  key={`controls-${idx}`}
                  className={idx % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}
                >
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={row.hazard}
                      onChange={(e) => updateControlsRow(idx, { hazard: e.target.value })}
                      placeholder="Hazard"
                      className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <textarea
                      value={row.existingControls}
                      onChange={(e) => updateControlsRow(idx, { existingControls: e.target.value })}
                      placeholder="Existing controls..."
                      rows={2}
                      className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent resize-none min-h-[2.5rem]"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <textarea
                      value={row.additionalControlsRequired}
                      onChange={(e) =>
                        updateControlsRow(idx, { additionalControlsRequired: e.target.value })
                      }
                      placeholder="Additional controls required..."
                      rows={2}
                      className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent resize-none min-h-[2.5rem]"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <select
                      value={row.controlType}
                      onChange={(e) => updateControlsRow(idx, { controlType: e.target.value })}
                      className="w-full h-9 px-2 rounded bg-black border border-gray-700 text-xs md:text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                    >
                      <option value="">—</option>
                      <option value="E">E (Elimination)</option>
                      <option value="S">S (Substitution)</option>
                      <option value="Eng">Eng (Engineering)</option>
                      <option value="A">A (Administrative)</option>
                      <option value="PPE">PPE (Personal Protective Equipment)</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={() =>
              setControlsRows((prev) => [
                ...prev,
                { hazard: '', existingControls: '', additionalControlsRequired: '', controlType: '' },
              ])
            }
            className="px-4 py-2 rounded bg-gray-800 text-gray-100 text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            + Add Control Row
          </button>
        </div>
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={removeLastControlsRow}
            disabled={!canRemoveControlsRow}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              canRemoveControlsRow
                ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                : 'bg-gray-900 text-gray-500 cursor-not-allowed'
            }`}
          >
            Remove Last Control Row
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Step 4 - Emergency Preparedness</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">
              Nearest first aid attendant &amp; location
            </label>
            <input
              type="text"
              value={projectInfo.firstAidAttendantLocation || ''}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, firstAidAttendantLocation: e.target.value }))
              }
              placeholder="Name & location"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Nearest fire extinguisher</label>
            <input
              type="text"
              value={projectInfo.fireExtinguisher || ''}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, fireExtinguisher: e.target.value }))
              }
              placeholder="Location / type"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1">Emergency exit/egress route</label>
            <input
              type="text"
              value={projectInfo.emergencyExitEgressRoute || ''}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, emergencyExitEgressRoute: e.target.value }))
              }
              placeholder="Route / description"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1">Site address / GPS coordinates</label>
            <input
              type="text"
              value={projectInfo.siteAddressGps || ''}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, siteAddressGps: e.target.value }))
              }
              placeholder="Address or GPS"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <div className="text-xs font-medium text-gray-300 mb-2">Emergency contact numbers posted</div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-200">
                <input
                  type="radio"
                  name="emergency-contacts-posted"
                  checked={projectInfo.emergencyContactsPosted === 'yes'}
                  onChange={() => setProjectInfo((p) => ({ ...p, emergencyContactsPosted: 'yes' }))}
                />
                Yes
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-200">
                <input
                  type="radio"
                  name="emergency-contacts-posted"
                  checked={projectInfo.emergencyContactsPosted === 'no'}
                  onChange={() => setProjectInfo((p) => ({ ...p, emergencyContactsPosted: 'no' }))}
                />
                No
              </label>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Step 5 - Sign-Off</h3>
        <p className="text-xs text-gray-300 mb-4">
          I have reviewed the hazards, risks, and controls for this job. I understand the safe work procedures and will follow them.
        </p>

        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Name (Print) / Signature</p>
            <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
          </div>
          <input
            type="text"
            value={projectInfo.signOffNamePrint || ''}
            onChange={(e) =>
              setProjectInfo((p) => ({ ...p, signOffNamePrint: e.target.value }))
            }
            placeholder="Name"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full"
          />
          <HandwrittenSignatureField
            textValue={projectInfo.signOffSignature || ''}
            imageValue={projectInfo.signOffSignatureImage || ''}
            onTextChange={(value) => setProjectInfo((p) => ({ ...p, signOffSignature: value }))}
            onImageChange={(value) => setProjectInfo((p) => ({ ...p, signOffSignatureImage: value }))}
            dateValue={projectInfo.signOffDate || ''}
            onDateChange={(value) => setProjectInfo((p) => ({ ...p, signOffDate: value }))}
            placeholder="Type signature"
            inputClassName="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full"
          />
        </div>

        <div className="border-t border-gray-700 pt-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-200">Supervisor Authorization</h4>
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Name / Signature</p>
            <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
          </div>
          <input
            type="text"
            value={projectInfo.supervisorAuthName || ''}
            onChange={(e) =>
              setProjectInfo((p) => ({ ...p, supervisorAuthName: e.target.value }))
            }
            placeholder="Supervisor name"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full"
          />
          <HandwrittenSignatureField
            textValue={projectInfo.supervisorAuthSignature || ''}
            imageValue={projectInfo.supervisorAuthSignatureImage || ''}
            onTextChange={(value) =>
              setProjectInfo((p) => ({ ...p, supervisorAuthSignature: value }))
            }
            onImageChange={(value) =>
              setProjectInfo((p) => ({ ...p, supervisorAuthSignatureImage: value }))
            }
            dateValue={projectInfo.supervisorAuthDate || ''}
            onDateChange={(value) =>
              setProjectInfo((p) => ({ ...p, supervisorAuthDate: value }))
            }
            placeholder="Type signature"
            inputClassName="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Document Control</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1">Form No.</label>
            <input
              type="text"
              value="JHA-BC-01"
              readOnly
              className="px-3 py-2 rounded bg-gray-950 border border-gray-800 text-sm text-gray-100 focus:outline-none"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Revision</label>
            <input
              type="text"
              value={projectInfo.documentRevision || ''}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, documentRevision: e.target.value }))
              }
              placeholder="Revision"
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-300 mb-1">Effective Date</label>
            <input
              type="date"
              value={projectInfo.effectiveDate || ''}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, effectiveDate: e.target.value }))
              }
              className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1">Retention</label>
            <textarea
              value={projectInfo.retentionPolicy || 'Keep completed forms on site for duration of project and per company policy.'}
              onChange={(e) =>
                setProjectInfo((p) => ({ ...p, retentionPolicy: e.target.value }))
              }
              rows={3}
              className="w-full px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
            />
          </div>
        </div>
      </div>

    </form>
  );
}

function JlgPreUseInspectionForm() {
  const sections = [
    {
      title: 'General Requirements',
      items: [
        'Inspect before first operation or operator change',
        'Report any unsafe defects to supervisor/employer',
        'Maintain records of defects for at least 2 years',
      ],
    },
    {
      title: 'Visual and Structural Inspection',
      items: [
        'Ensure all surfaces are free of oil, fuel, battery fluid, dirt, and debris',
        'Check for dents, cracks, and other damage',
        'Confirm all decals are legible and in correct locations',
      ],
    },
    {
      title: 'Hazardous Materials & Environmental Controls',
      items: [
        'SDS sheets available for all chemicals',
        'Spill kits available and accessible',
        'Waste bins available and disposed appropriately',
        'Dust, fumes, noise, and vibration levels monitored',
      ],
    },
    {
      title: 'Fluid Levels',
      items: [
        'Check fuel, DEF, hydraulic oil, engine oil, and coolant levels',
        'Wipe dirt from ports before opening',
        'Use correct fluid types and follow service intervals',
      ],
    },
    {
      title: 'Attachments and Accessories',
      items: [
        'Ensure correct capacity charts are installed',
        'Inspect attachments per their specific Operation & Safety Manual',
      ],
    },
    {
      title: 'Walk-Around Inspection',
      items: [
        'Inspect boom sections, axles, wheels, mirrors, cab, electrical system',
        'Check engine components, frame-level cylinder, lights, cameras, sensors',
        'Verify audible warning devices are functional',
      ],
    },
    {
      title: 'Operational Check',
      items: [
        'Warm up engine in a clear area',
        'Test heater, A/C, wipers, lighting systems, mirrors',
        'Check brakes, steering, gear shifting, horn, backup alarm',
        'Test emergency stop button and remote boom control',
      ],
    },
    {
      title: 'Cab Safety',
      items: [
        'Ensure seat belts are present and in good condition',
        'Cab surfaces free of grease, tools, and debris',
      ],
    },
    {
      title: 'Additional WorkSafeBC Requirements',
      items: [
        'Use seat belts when in motion or during unstable operations',
        'Maintain clear visibility (mirrors, windows)',
        'Acknowledge workers before moving (eye contact)',
      ],
    },
  ];

  let globalIndex = 0;

  return (
    <form className="space-y-8">
      {sections.map((section) => (
        <div key={section.title}>
          <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
          <div className="overflow-x-auto border border-gray-800 rounded-lg">
            <table className="min-w-full text-xs md:text-sm table-fixed">
              <colgroup>
                <col className="w-1/2" />
                <col className="w-16" />
                <col className="w-16" />
                <col className="w-1/3" />
              </colgroup>
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Item</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Yes</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">No</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-300">Notes / Corrective Actions</th>
                </tr>
              </thead>
              <tbody>
                {section.items.map((label) => {
                  const rowIndex = globalIndex;
                  globalIndex += 1;
                  return (
                    <tr
                      key={label}
                      className={rowIndex % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}
                    >
                      <td className="px-3 py-2 align-top text-gray-200">
                        {rowIndex + 1}. {label}
                      </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex justify-center">
                      <input
                        type="radio"
                        name={`jlg-item-${rowIndex}`}
                        className="h-5 w-5 text-emerald-400 bg-black border-gray-700"
                      />
                    </div>
                      </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex justify-center">
                      <input
                        type="radio"
                        name={`jlg-item-${rowIndex}`}
                        className="h-5 w-5 text-red-400 bg-black border-gray-700"
                      />
                    </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          type="text"
                          placeholder="Enter text here"
                          className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

    </form>
  );
}

function WeeklyInspectionForm({ projectName }) {
  const { name: profileName } = useUserProfile();
  const [inspector, setInspector] = useState(profileName);
  const [inspectionDate, setInspectionDate] = useState('');

  useEffect(() => {
    if (!profileName) return;
    setInspector((current) => fillIfEmpty(current, profileName));
  }, [profileName]);

  const items = [
    'Electrical & Fire Safety',
    'Hazardous Materials & Environmental Controls',
    'Smoking in designated areas',
    'SDS sheets available for all chemicals',
    'Spill kits available & accessible',
    'Waste bins available & disposed appropriately',
    'Dust, fumes, noise, vibration levels monitored',
    'Temporary wiring secured & protected',
    'Electrical panels accessible & labeled',
    'No exposed live wires',
    'Flammable materials stored properly',
    'Workers using fall protection/arrest systems when required',
    'Scaffolding erected & inspected to regulation',
    'Ladders are in good condition & used correctly',
    'Guardrails and toeboards in place',
    'Weather-related hazards addressed (ice, mud, heat)',
    'Designated muster point – all workers aware of location',
    'Tools properly stored & in good working condition',
    'Equipment inspections up to date',
    'Lockout/Tagout procedures followed',
    'All workers wearing required PPE',
    'First aid kits stocked and accessible',
    'Safety signage posted and visible',
    'Workers trained in site-specific safety protocols',
    'Heavy machinery operated by certified personnel',
    'Fire extinguishers accessible & inspected',
    'Site access is clearly marked',
    'Walkways & work areas free of tripping hazards & debris',
    'Adequate lighting in all work zones',
  ];

  return (
    <form className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Project Name</label>
          <input
            type="text"
            defaultValue={projectName}
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Date</label>
          <input
            type="date"
            value={inspectionDate}
            onChange={(e) => setInspectionDate(e.target.value)}
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Inspector</label>
          <input
            type="text"
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            placeholder="Inspector name"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">General Site Conditions</h3>
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="min-w-full text-xs md:text-sm">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Item</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Yes</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">No</th>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Notes / Corrective Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((label, index) => (
                <tr key={label} className={index % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}>
                  <td className="px-3 py-2 align-top text-gray-200">
                    <span className="form-item-number">{index + 1}. </span>
                    {label}
                  </td>
                  <td className="px-3 py-2 text-center align-top">
                    <input
                      type="radio"
                      name={`item-${index}`}
                      className="h-5 w-5 text-emerald-400 bg-black border-gray-700"
                    />
                  </td>
                  <td className="px-3 py-2 text-center align-top">
                    <input
                      type="radio"
                      name={`item-${index}`}
                      className="h-5 w-5 text-red-400 bg-black border-gray-700"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      placeholder="Enter text here"
                      className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </form>
  );
}

function PrimeContractorChecklistForm() {
  const items = [
    'Has a Notice of Project (NOP) been completed and is a copy on site?',
    'Is there a signed, written agreement in place between the owner and the prime contractor?',
    'Has the owner provided information about any known hazards on the worksite?',
    'Has the prime contractor received the names of the designated supervisors for all the other companies or subcontractors working on that site?',
    'Are all companies on site registered with WorkSafeBC and in good standing?',
    'Has the prime contractor held safety meetings with the other companies on site in order to coordinate safety activities?',
    'Has the prime contractor checked the other companies’ safety programs and procedures and are they adequate for the planned work?',
    'Does the prime contractor have a system in place to identify and control hazards?',
    'Does the prime contractor have a way to communicate the hazards and controls to all workers on the site?',
    'Does the prime contractor have a system in place to make sure regular inspections are completed on the site, equipment and workers?',
    'Does the prime contractor have a system in place to make sure all workers on site receive a safety orientation before they start work?',
    'Is there an Emergency Response Plan and emergency equipment for the site and has it been tested?',
    'Has a First Aid Assessment been completed for the site and have all the first aid requirements for equipment and first aid attendants been met?',
    'Is there an incident reporting system in place so all the companies on site report their incidents to the prime contractor?',
  ];

  return (
    <form className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Date</label>
          <input
            type="date"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Location</label>
          <input
            type="text"
            placeholder="Enter location"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-300 mb-1">Prime Contractor</label>
          <input
            type="text"
            placeholder="Prime contractor name"
            className="px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Prime Contractor Requirements</h3>
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="min-w-full text-xs md:text-sm">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Item</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">Yes</th>
                <th className="px-3 py-2 text-center font-medium text-gray-300 w-20">No</th>
                <th className="px-3 py-2 text-left font-medium text-gray-300">Action Required</th>
              </tr>
            </thead>
            <tbody>
              {items.map((label, index) => (
                <tr key={label} className={index % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/60'}>
                  <td className="px-3 py-2 align-top text-gray-200">
                    <span className="form-item-number">{index + 1}. </span>
                    {label}
                  </td>
                  <td className="px-3 py-2 text-center align-top">
                    <input
                      type="radio"
                      name={`pc-item-${index}`}
                      className="h-5 w-5 text-emerald-400 bg-black border-gray-700"
                    />
                  </td>
                  <td className="px-3 py-2 text-center align-top">
                    <input
                      type="radio"
                      name={`pc-item-${index}`}
                      className="h-5 w-5 text-red-400 bg-black border-gray-700"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      placeholder="Enter text here"
                      className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </form>
  );
}

function FirstAidRecordForm({ projectName }) {
  const { name: profileName, signature: rawProfileSignature } = useUserProfile();
  const profileSig = splitProfileSignature(rawProfileSignature);
  const inputClass =
    'px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full';
  const textareaClass = `${inputClass} block w-full min-h-[72px] resize-y`;

  const [meta, setMeta] = useState({
    firstAndLastName: '',
    occupation: '',
    injuryDate: '',
    injuryTime: '',
    injuryTimeAmPm: 'am',
    initialReportDate: '',
    initialReportTime: '',
    initialReportAmPm: 'am',
    followUpReportDate: '',
    followUpReportTime: '',
    followUpReportAmPm: 'am',
    initialSequenceNumber: '',
    subsequentSequenceNumbers: '',
  });

  const [descriptions, setDescriptions] = useState({
    howOccurred: '',
    natureOfInjury: '',
    treatmentGiven: '',
    witnessNames: '',
    workerArrangements: '',
  });

  const [witnesses, setWitnesses] = useState({ witness1: '', witness2: '' });

  const [followUp, setFollowUp] = useState({
    providedHandout: '',
    discussedReturnToWork: '',
    sentFormToMedicalAid: '',
    attendantName: profileName,
    attendantSignature: profileSig.text,
    attendantSignatureImage: profileSig.image,
    patientSignature: '',
    patientSignatureImage: '',
  });

  useEffect(() => {
    if (!profileName && !rawProfileSignature) return;
    const split = splitProfileSignature(rawProfileSignature);
    setFollowUp((p) => ({
      ...p,
      attendantName: fillIfEmpty(p.attendantName, profileName),
      attendantSignature: fillIfEmpty(p.attendantSignature, split.text),
      attendantSignatureImage: fillIfEmpty(p.attendantSignatureImage, split.image),
    }));
  }, [profileName, rawProfileSignature]);

  const setMetaField = (field, value) => setMeta((p) => ({ ...p, [field]: value }));
  const setDescField = (field, value) => setDescriptions((p) => ({ ...p, [field]: value }));

  const YesNoField = ({ label, name, value, onChange }) => (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-gray-300">{label}</span>
      <div className="flex gap-4">
        {['yes', 'no'].map((opt) => (
          <label key={opt} className="inline-flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
            <input
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="h-4 w-4 text-amber-300 bg-black border-gray-600 focus:ring-amber-400"
            />
            <span className="capitalize">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <form className="space-y-6 w-full">
      <p className="text-xs text-gray-400">
        Based on <span className="text-gray-300">First Aid Record {projectName}</span> (WorkSafeBC-style record)
      </p>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30">
      <div className="overflow-x-auto border-b border-gray-800">
        <table className="min-w-full text-xs md:text-sm">
          <tbody>
            <tr className="bg-gray-950 border-b border-gray-800">
              <td className="px-3 py-3 align-top w-1/2 border-r border-gray-800">
                <label className="text-xs font-medium text-gray-300 mb-1 block">First and last name</label>
                <input
                  type="text"
                  value={meta.firstAndLastName}
                  onChange={(e) => setMetaField('firstAndLastName', e.target.value)}
                  className={inputClass}
                />
              </td>
              <td className="px-3 py-3 align-top w-1/2">
                <label className="text-xs font-medium text-gray-300 mb-1 block">Occupation</label>
                <input
                  type="text"
                  value={meta.occupation}
                  onChange={(e) => setMetaField('occupation', e.target.value)}
                  className={inputClass}
                />
              </td>
            </tr>
            <tr className="bg-gray-900/60 border-b border-gray-800">
              <td className="px-3 py-3 align-top border-r border-gray-800">
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Date of injury, exposure, or illness (yyyy-mm-dd)
                </label>
                <input
                  type="date"
                  value={meta.injuryDate}
                  onChange={(e) => setMetaField('injuryDate', e.target.value)}
                  className={inputClass}
                />
              </td>
              <td className="px-3 py-3 align-top">
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Time of injury, exposure, or illness (hh:mm)
                </label>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="time"
                    value={meta.injuryTime}
                    onChange={(e) => setMetaField('injuryTime', e.target.value)}
                    className={`${inputClass} flex-1 min-w-[120px]`}
                  />
                  <select
                    value={meta.injuryTimeAmPm}
                    onChange={(e) => setMetaField('injuryTimeAmPm', e.target.value)}
                    className={`${inputClass} w-24`}
                  >
                    <option value="am">a.m.</option>
                    <option value="pm">p.m.</option>
                  </select>
                </div>
              </td>
            </tr>
            <tr className="bg-gray-950 border-b border-gray-800">
              <td className="px-3 py-3 align-top border-r border-gray-800">
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Initial reporting date and time (yyyy-mm-dd) (hh:mm)
                </label>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="date"
                    value={meta.initialReportDate}
                    onChange={(e) => setMetaField('initialReportDate', e.target.value)}
                    className={`${inputClass} flex-1 min-w-[130px]`}
                  />
                  <input
                    type="time"
                    value={meta.initialReportTime}
                    onChange={(e) => setMetaField('initialReportTime', e.target.value)}
                    className={`${inputClass} flex-1 min-w-[120px]`}
                  />
                  <select
                    value={meta.initialReportAmPm}
                    onChange={(e) => setMetaField('initialReportAmPm', e.target.value)}
                    className={`${inputClass} w-24`}
                  >
                    <option value="am">a.m.</option>
                    <option value="pm">p.m.</option>
                  </select>
                </div>
              </td>
              <td className="px-3 py-3 align-top">
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Follow-up report date and time (yyyy-mm-dd) (hh:mm)
                </label>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="date"
                    value={meta.followUpReportDate}
                    onChange={(e) => setMetaField('followUpReportDate', e.target.value)}
                    className={`${inputClass} flex-1 min-w-[130px]`}
                  />
                  <input
                    type="time"
                    value={meta.followUpReportTime}
                    onChange={(e) => setMetaField('followUpReportTime', e.target.value)}
                    className={`${inputClass} flex-1 min-w-[120px]`}
                  />
                  <select
                    value={meta.followUpReportAmPm}
                    onChange={(e) => setMetaField('followUpReportAmPm', e.target.value)}
                    className={`${inputClass} w-24`}
                  >
                    <option value="am">a.m.</option>
                    <option value="pm">p.m.</option>
                  </select>
                </div>
              </td>
            </tr>
            <tr className="bg-gray-900/60">
              <td className="px-3 py-3 align-top border-r border-gray-800">
                <label className="text-xs font-medium text-gray-300 mb-1 block">Initial report sequence number</label>
                <input
                  type="text"
                  value={meta.initialSequenceNumber}
                  onChange={(e) => setMetaField('initialSequenceNumber', e.target.value)}
                  className={inputClass}
                />
              </td>
              <td className="px-3 py-3 align-top">
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Subsequent report sequence number(s)
                </label>
                <input
                  type="text"
                  value={meta.subsequentSequenceNumbers}
                  onChange={(e) => setMetaField('subsequentSequenceNumbers', e.target.value)}
                  className={inputClass}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="space-y-4 p-4 md:p-5 w-full">
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">
            Description of how the injury, exposure, or illness occurred (What happened?)
          </label>
          <textarea
            value={descriptions.howOccurred}
            onChange={(e) => setDescField('howOccurred', e.target.value)}
            className={textareaClass}
            placeholder="Describe what happened..."
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">
            Description of the nature of the injury, exposure, or illness (What did you see? i.e., signs and symptoms)
          </label>
          <textarea
            value={descriptions.natureOfInjury}
            onChange={(e) => setDescField('natureOfInjury', e.target.value)}
            className={textareaClass}
            placeholder="Signs and symptoms observed..."
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">
            Description of the treatment given (What did you do?)
          </label>
          <textarea
            value={descriptions.treatmentGiven}
            onChange={(e) => setDescField('treatmentGiven', e.target.value)}
            className={textareaClass}
            placeholder="Treatment provided..."
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">Names of witnesses</label>
          <textarea
            value={descriptions.witnessNames}
            onChange={(e) => setDescField('witnessNames', e.target.value)}
            className={`${textareaClass} min-h-[56px]`}
            placeholder="List witness names..."
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">
            Arrangements made relating to the worker (return to work / medical aid / ambulance / follow-up)
          </label>
          <textarea
            value={descriptions.workerArrangements}
            onChange={(e) => setDescField('workerArrangements', e.target.value)}
            className={textareaClass}
            placeholder="Return to work, medical aid, ambulance, follow-up arrangements..."
          />
        </div>
      </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 border border-gray-800 rounded-lg p-4 md:p-5 bg-gray-900/30">
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">1. Witness</label>
          <input
            type="text"
            value={witnesses.witness1}
            onChange={(e) => setWitnesses((p) => ({ ...p, witness1: e.target.value }))}
            className={inputClass}
            placeholder="Witness name"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">2. Witness</label>
          <input
            type="text"
            value={witnesses.witness2}
            onChange={(e) => setWitnesses((p) => ({ ...p, witness2: e.target.value }))}
            className={inputClass}
            placeholder="Witness name"
          />
        </div>
      </div>

      <div className="border border-gray-800 rounded-lg p-4 space-y-4 bg-gray-900/40">
        <div className="grid gap-4 md:grid-cols-2">
          <YesNoField
            label="Provided worker handout"
            name="providedHandout"
            value={followUp.providedHandout}
            onChange={(v) => setFollowUp((p) => ({ ...p, providedHandout: v }))}
          />
          <YesNoField
            label="Discussed recommendations for return to work"
            name="discussedReturnToWork"
            value={followUp.discussedReturnToWork}
            onChange={(v) => setFollowUp((p) => ({ ...p, discussedReturnToWork: v }))}
          />
        </div>
        <YesNoField
          label="Sent a form with the worker to medical aid to assist in return to work and follow-up"
          name="sentFormToMedicalAid"
          value={followUp.sentFormToMedicalAid}
          onChange={(v) => setFollowUp((p) => ({ ...p, sentFormToMedicalAid: v }))}
        />
        <div className="grid gap-4 md:grid-cols-2 pt-2 border-t border-gray-800">
          <div>
            <label className="text-xs font-medium text-gray-300 mb-1 block">
              First aid attendant&apos;s name (please print or type)
            </label>
            <input
              type="text"
              value={followUp.attendantName}
              onChange={(e) => setFollowUp((p) => ({ ...p, attendantName: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-300 mb-1 block">First aid attendant&apos;s signature</label>
            <HandwrittenSignatureField
              textValue={followUp.attendantSignature}
              imageValue={followUp.attendantSignatureImage || ''}
              onTextChange={(value) => setFollowUp((p) => ({ ...p, attendantSignature: value }))}
              onImageChange={(value) => setFollowUp((p) => ({ ...p, attendantSignatureImage: value }))}
              placeholder="Type signature"
              inputClassName={inputClass}
            />
          </div>
        </div>
        <div className="pt-2 border-t border-gray-800">
          <label className="text-xs font-medium text-gray-300 mb-1 block">Patient&apos;s signature</label>
          <HandwrittenSignatureField
            textValue={followUp.patientSignature}
            imageValue={followUp.patientSignatureImage || ''}
            onTextChange={(value) => setFollowUp((p) => ({ ...p, patientSignature: value }))}
            onImageChange={(value) => setFollowUp((p) => ({ ...p, patientSignatureImage: value }))}
            placeholder="Type signature"
            inputClassName={inputClass}
          />
        </div>
      </div>

    </form>
  );
}

const ORIENTATION_WORKER_SIGNATURE_COUNT = 7;

const ORIENTATION_SECTIONS = [
  {
    number: 1,
    title: 'Worker Rights and Responsibilities (OHSR Part 3)',
    subsections: [
      {
        heading: 'Three Basic Rights:',
        items: [
          'Right to Know: About hazards in the workplace.',
          'Right to Participate: In health and safety activities and committees.',
          'Right to Refuse Unsafe Work: Without fear of retaliation. (OHSR 3.12)',
        ],
      },
      {
        heading: 'Worker Responsibilities:',
        items: [
          'Follow safe work procedures.',
          'Use PPE as required-hard hat, steel toed boots',
          'Be alert, report hazards or unsafe conditions, injury, illness and near miss.',
          'Cooperate with health and safety representatives and WorkSafeBC officers.',
        ],
      },
      {
        heading: 'Employer Responsibilities:',
        items: [
          'Ensure a safe work environment.',
          'Provide training and supervision.',
          'Provide and maintain PPE=high vis vest, safety glasses, dust masks. Employee provides their own CSA approved safety boots and hard hat',
          'Inform workers of hazards they may be exposed to in the workplace:',
        ],
      },
      {
        heading: null,
        items: [
          'Working alone or in isolation-there are procedures for this (OHSR 4.20.1)',
          'Bullying and harassment or violence in the workplace (OHSR 4.27)',
          'Theft, robbery, assault or confrontation',
          'Workplace hazards that are mitigated with controls and documented on the daily FLHA (OHSR 3.5 & 3.9)',
        ],
      },
    ],
  },
  {
    number: 2,
    title: 'Health and Safety Rules (OHSR 3.1)',
    items: [
      'Attend toolbox talks and safety meetings.',
      'Be alert to hazards - Report all incidents, near misses, and injuries immediately.',
      'Maintain good housekeeping—keep work areas clean and organized.',
      'Never work under the influence of drugs or alcohol. (OHSR 4.19)',
      'Take reasonable care to protect your own safety and the safety of others.',
      'You will be provided the name and contact information of your supervisor',
      "You will be provided the worker H & S representative's contact information",
    ],
  },
  {
    number: 3,
    title: 'Personal Protective Equipment (PPE)(OHSR pt 8)',
    subsections: [
      {
        heading: 'Minimum required PPE on BC construction sites:',
        items: [
          'CSA-approved hard hat and CSA-approved steel-toe boots',
          'High-visibility vest or clothing',
          'Safety glasses or goggles (as needed)',
          'Hearing protection (as needed)',
          'Gloves (task-specific)',
          'Respirators (if required by hazard assessment)',
        ],
      },
    ],
  },
  {
    number: 4,
    title: 'Fall Protection (OHSR Part 11)',
    subsections: [
      {
        heading: 'Required when working:',
        items: [
          'At heights of 3 metres (10 feet) or more',
          'Near open edges, floor openings, or unguarded platforms',
        ],
      },
      {
        heading: 'Fall Protection Systems:',
        items: ['Guardrails', 'Fall restraint systems', 'Fall arrest systems', 'Safety nets'],
      },
    ],
    footer: 'A certificate of training is required before using fall restraint/arrest equipment.',
  },
  {
    number: 5,
    title: 'First Aid (OHSR 3.14)',
    items: ['First aid attendants and kits must be available on-site.'],
    subsections: [
      {
        heading: 'Know the location of:',
        items: [
          'First aid station and how to contact FAA',
          'First aid attendant',
          'Emergency contact numbers',
        ],
      },
    ],
    footer: 'All injuries must be reported and recorded, no matter how minor.',
  },
  {
    number: 6,
    title: 'Emergency Procedures (OHSR 4.13-4.18)',
    items: ['Know the site-specific emergency plan.'],
    subsections: [
      {
        heading: 'Familiarize yourself with:',
        items: ['Muster point', 'Fire extinguishers (PASS)', 'Evacuation routes', 'Emergency contacts'],
      },
    ],
    footer: 'Participate in emergency drills',
  },
  {
    number: 7,
    title: 'Health and Safety Program-is located on the site board or in the site binder',
    subsections: [
      {
        heading: 'Includes:',
        items: [
          'Safety policies and procedures',
          'Hazard identification and control',
          'Training and orientation',
          'Incident investigation',
          'Joint Health and Safety Committee or worker representative',
        ],
      },
    ],
  },
  {
    number: 8,
    title: 'WHMIS 2015 (Workplace Hazardous Materials Information System)',
    items: ['All workers in Canada must be trained in WHMIS to understand:'],
    subsections: [
      {
        heading: null,
        items: [
          'Hazard pictograms',
          'Safety Data Sheets (SDS)',
          'Labels (supplier and workplace)',
          'Safe handling, storage, and disposal of hazardous products',
          'Location of spill kit',
        ],
      },
    ],
  },
];

const FLHA_PPE_ITEMS = [
  'Hard Hat',
  'Steel-Toe Boots',
  'High-Visibility Vest',
  'Safety Glasses',
  'Hearing Protection',
  'Gloves',
  'Respirator (if required)',
];

const FLHA_WORKER_ROW_COUNT = 20;

function SiteWorkerOrientationForm({ projectName }) {
  const { name: profileName, signature: rawProfileSignature } = useUserProfile();
  const profileSig = splitProfileSignature(rawProfileSignature);
  const profileText = profileSignatureText(rawProfileSignature);
  const companyLabel = (projectName || 'Company').trim() || 'Company';
  const inputClass =
    'px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full';
  const textareaClass = `${inputClass} block min-h-[72px] resize-y`;

  const [medicalConditionsFaa, setMedicalConditionsFaa] = useState('');
  const [siteSupervisor, setSiteSupervisor] = useState({
    signature: profileSig.text,
    signatureImage: profileSig.image,
    date: '',
  });
  const [orientationWorkers, setOrientationWorkers] = useState(() =>
    Array.from({ length: ORIENTATION_WORKER_SIGNATURE_COUNT }, (_, index) => ({
      signatureMode: 'type',
      signatureCoName: index === 0 && profileName
        ? `${profileName}${profileText ? ` / ${profileText}` : ''}`
        : '',
      signatureImage: index === 0 ? profileSig.image : '',
      date: '',
    }))
  );
  const [orientationProjectName, setOrientationProjectName] = useState(projectName || '');

  useEffect(() => {
    if (projectName) {
      setOrientationProjectName((prev) => fillIfEmpty(prev, projectName));
    }
  }, [projectName]);
  useEffect(() => {
    if (!profileName && !rawProfileSignature) return;
    const split = splitProfileSignature(rawProfileSignature);
    const text = profileSignatureText(rawProfileSignature);
    setSiteSupervisor((p) => ({
      ...p,
      signature: fillIfEmpty(p.signature, split.text),
      signatureImage: fillIfEmpty(p.signatureImage, split.image),
    }));
    setOrientationWorkers((prev) => {
      if (!prev[0]) return prev;
      const combined = [profileName, text].filter(Boolean).join(' / ');
      const next = [...prev];
      next[0] = {
        ...next[0],
        signatureCoName: fillIfEmpty(next[0].signatureCoName, combined),
        signatureImage: fillIfEmpty(next[0].signatureImage, split.image),
      };
      return next;
    });
  }, [profileName, rawProfileSignature]);

  const setOrientationWorker = (index, field, value) => {
    setOrientationWorkers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const renderBullets = (items) => (
    <ul className="list-disc list-inside space-y-1 text-sm text-gray-300 ml-1">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );

  return (
    <form className="space-y-6 w-full">
      <p className="text-lg font-semibold text-white">New and/or Young Worker Site Orientation</p>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-6">
      <div className="space-y-6">
        {ORIENTATION_SECTIONS.map((section) => (
          <div key={section.number} className="space-y-3">
            <h4 className="text-sm font-medium text-amber-200/90">
              <span className="form-heading-number">{section.number}. </span>
              {section.title}
            </h4>
            {section.items && renderBullets(section.items)}
            {section.subsections?.map((sub, subIndex) => (
              <div key={sub.heading || `${section.number}-sub-${subIndex}`} className="space-y-1">
                {sub.heading ? (
                  <p className="text-xs font-medium text-gray-300">{sub.heading}</p>
                ) : null}
                {renderBullets(sub.items)}
              </div>
            ))}
            {section.footer && <p className="text-sm text-gray-300">{section.footer}</p>}
            {section.number === 5 && (
              <div className="pt-2">
                <label className="text-xs font-medium text-gray-300 mb-1 block">
                  Any medical conditions you&apos;d like the FAA to be aware of?
                </label>
                <textarea
                  value={medicalConditionsFaa}
                  onChange={(e) => setMedicalConditionsFaa(e.target.value)}
                  className={textareaClass}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-gray-800 space-y-4">
        <h4 className="text-sm font-medium text-white">
          Worker PPE agreement and acknowledgement of Orientation
        </h4>
        <p className="text-sm text-gray-300 leading-relaxed">
          As a site worker or contractor on{' '}
          <span className="font-semibold text-white">{companyLabel}</span> projects, I acknowledge
          my full &amp; complete understanding and compliance of all items listed in this Orientation.
        </p>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">
              Site Safety Officer OR Site Supervisor
            </p>
            <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
          </div>
          <HandwrittenSignatureField
            textValue={siteSupervisor.signature}
            imageValue={siteSupervisor.signatureImage || ''}
            onTextChange={(value) => setSiteSupervisor((p) => ({ ...p, signature: value }))}
            onImageChange={(value) => setSiteSupervisor((p) => ({ ...p, signatureImage: value }))}
            dateValue={siteSupervisor.date}
            onDateChange={(value) => setSiteSupervisor((p) => ({ ...p, date: value }))}
            placeholder="Type name / signature"
            inputClassName={inputClass}
          />
        </div>

        <div className="space-y-0">
          <div className="mb-2 flex items-center gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">
              Worker/Contractor Signature
            </p>
            <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
          </div>
          {orientationWorkers.map((row, index) => (
            <div
              key={index}
              className="border-b border-gray-700 py-3 last:border-b-0 last:pb-0"
            >
              <HandwrittenSignatureField
                textValue={row.signatureCoName}
                imageValue={row.signatureImage || ''}
                onTextChange={(value) => setOrientationWorker(index, 'signatureCoName', value)}
                onImageChange={(value) => setOrientationWorker(index, 'signatureImage', value)}
                dateValue={row.date}
                onDateChange={(value) => setOrientationWorker(index, 'date', value)}
                placeholder="Type name / company"
              />
            </div>
          ))}
        </div>

        <div>
          <label className="text-xs font-medium text-gray-300 mb-1 block">Project Name</label>
          <input
            type="text"
            value={orientationProjectName}
            onChange={(e) => setOrientationProjectName(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      </div>

    </form>
  );
}

function FieldLevelHazardAssessmentForm({ projectName }) {
  const { name: profileName, signature: rawProfileSignature } = useUserProfile();
  const profileSig = splitProfileSignature(rawProfileSignature);
  const inputClass =
    'px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full';
  const textareaClass = `${inputClass} block min-h-[72px] resize-y`;

  const [projectDetails, setProjectDetails] = useState({
    projectName: projectName || '',
    location: '',
    date: '',
    worksiteRepresentative: profileName,
    locationOfFaSupplies: '',
    musterPoint: '',
  });

  const [taskDescription, setTaskDescription] = useState('');
  const [identifiedHazards, setIdentifiedHazards] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [controlMeasures, setControlMeasures] = useState('');
  const [ppe, setPpe] = useState(() =>
    FLHA_PPE_ITEMS.reduce((acc, item) => ({ ...acc, [item]: false }), {})
  );
  const [toolboxTalkTopic, setToolboxTalkTopic] = useState('');
  const [workers, setWorkers] = useState(() =>
    Array.from({ length: FLHA_WORKER_ROW_COUNT }, (_, index) => ({
      name: index === 0 ? profileName : '',
      signature: index === 0 ? profileSig.text : '',
      signatureImage: index === 0 ? profileSig.image : '',
      date: '',
    }))
  );
  const [worksiteRep, setWorksiteRep] = useState({
    name: profileName,
    signature: profileSig.text,
    signatureImage: profileSig.image,
    date: '',
  });

  useEffect(() => {
    if (!profileName && !rawProfileSignature) return;
    const split = splitProfileSignature(rawProfileSignature);
    setProjectDetails((p) => ({
      ...p,
      worksiteRepresentative: fillIfEmpty(p.worksiteRepresentative, profileName),
    }));
    setWorkers((prev) => {
      if (!prev[0]) return prev;
      const next = [...prev];
      next[0] = {
        ...next[0],
        name: fillIfEmpty(next[0].name, profileName),
        signature: fillIfEmpty(next[0].signature, split.text),
        signatureImage: fillIfEmpty(next[0].signatureImage, split.image),
      };
      return next;
    });
    setWorksiteRep((p) => ({
      ...p,
      name: fillIfEmpty(p.name, profileName),
      signature: fillIfEmpty(p.signature, split.text),
      signatureImage: fillIfEmpty(p.signatureImage, split.image),
    }));
  }, [profileName, rawProfileSignature]);

  const setDetail = (field, value) =>
    setProjectDetails((p) => ({ ...p, [field]: value }));

  const setWorker = (index, field, value) => {
    setWorkers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const SectionHeading = ({ number, children }) => (
    <h3 className="text-sm font-semibold text-white border-b border-gray-700 pb-2 mb-4">
      <span className="form-heading-number">{number}. </span>
      {children}
    </h3>
  );

  return (
    <form className="space-y-6 w-full">
      <p className="text-lg font-semibold text-white">Field Level Hazard Assessment</p>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <SectionHeading number={1}>Project Details</SectionHeading>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1 block">Project Name</label>
            <input
              type="text"
              value={projectDetails.projectName}
              onChange={(e) => setDetail('projectName', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1 block">Location</label>
            <input
              type="text"
              value={projectDetails.location}
              onChange={(e) => setDetail('location', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-300 mb-1 block">Date</label>
            <input
              type="date"
              value={projectDetails.date}
              onChange={(e) => setDetail('date', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-300 mb-1 block">Worksite Representative</label>
            <input
              type="text"
              value={projectDetails.worksiteRepresentative}
              onChange={(e) => setDetail('worksiteRepresentative', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1 block">Location of FA supplies</label>
            <input
              type="text"
              value={projectDetails.locationOfFaSupplies}
              onChange={(e) => setDetail('locationOfFaSupplies', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-300 mb-1 block">Muster Point</label>
            <input
              type="text"
              value={projectDetails.musterPoint}
              onChange={(e) => setDetail('musterPoint', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5">
        <SectionHeading number={2}>Task Description</SectionHeading>
        <label className="text-xs font-medium text-gray-300 mb-1 block">
          Describe the task(s) being performed:
        </label>
        <textarea
          value={taskDescription}
          onChange={(e) => setTaskDescription(e.target.value)}
          className={textareaClass}
        />
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5">
        <SectionHeading number={3}>Identified Hazards</SectionHeading>
        <label className="text-xs font-medium text-gray-300 mb-1 block">
          List all potential hazards associated with the task:
        </label>
        <textarea
          value={identifiedHazards}
          onChange={(e) => setIdentifiedHazards(e.target.value)}
          className={textareaClass}
        />
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5">
        <SectionHeading number={4}>Risk Level</SectionHeading>
        <label className="text-xs font-medium text-gray-300 mb-2 block">
          Assess the risk level (Low / Medium / High):
        </label>
        <div className="flex flex-wrap gap-6">
          {['Low', 'Medium', 'High'].map((level) => (
            <label key={level} className="inline-flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="radio"
                name="flha-risk-level"
                value={level}
                checked={riskLevel === level}
                onChange={() => setRiskLevel(level)}
                className="h-4 w-4 text-amber-300 bg-black border-gray-600 focus:ring-amber-400"
              />
              {level}
            </label>
          ))}
        </div>
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5">
        <SectionHeading number={5}>Control Measures</SectionHeading>
        <label className="text-xs font-medium text-gray-300 mb-1 block">
          Describe the control measures to mitigate the hazards:
        </label>
        <textarea
          value={controlMeasures}
          onChange={(e) => setControlMeasures(e.target.value)}
          className={textareaClass}
        />
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5">
        <SectionHeading number={6}>Personal Protective Equipment (PPE) Required</SectionHeading>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {FLHA_PPE_ITEMS.map((item) => (
            <label key={item} className="inline-flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="checkbox"
                checked={ppe[item]}
                onChange={(e) => setPpe((prev) => ({ ...prev, [item]: e.target.checked }))}
                className="h-4 w-4 rounded text-amber-300 bg-black border-gray-600 focus:ring-amber-400"
              />
              {item}
            </label>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-xs font-medium text-gray-300 mb-1 block">Toolbox Talk Topic:</label>
          <input
            type="text"
            value={toolboxTalkTopic}
            onChange={(e) => setToolboxTalkTopic(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5">
        <SectionHeading number={7}>Worker Signatures</SectionHeading>
        <div className="mb-2 flex items-center gap-2">
          <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Name / Signature</p>
          <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
        </div>
        {workers.map((row, index) => (
          <div key={index} className="border-b border-gray-700 py-3 last:border-b-0">
            <div className="mb-2">
              <input
                type="text"
                value={row.name}
                onChange={(e) => setWorker(index, 'name', e.target.value)}
                placeholder="Worker name"
                className="w-full px-2 py-1 rounded bg-black border border-gray-700 text-xs md:text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600"
              />
            </div>
            <HandwrittenSignatureField
              textValue={row.signature}
              imageValue={row.signatureImage || ''}
              onTextChange={(value) => setWorker(index, 'signature', value)}
              onImageChange={(value) => setWorker(index, 'signatureImage', value)}
              dateValue={row.date}
              onDateChange={(value) => setWorker(index, 'date', value)}
              placeholder="Type signature"
            />
          </div>
        ))}
        <div className="mt-6 space-y-2 border-t border-gray-800 pt-4">
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Worksite Rep / Signature</p>
            <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
          </div>
          <input
            type="text"
            value={worksiteRep.name}
            onChange={(e) => setWorksiteRep((p) => ({ ...p, name: e.target.value }))}
            placeholder="Worksite representative"
            className={inputClass}
          />
          <HandwrittenSignatureField
            textValue={worksiteRep.signature}
            imageValue={worksiteRep.signatureImage || ''}
            onTextChange={(value) => setWorksiteRep((p) => ({ ...p, signature: value }))}
            onImageChange={(value) => setWorksiteRep((p) => ({ ...p, signatureImage: value }))}
            dateValue={worksiteRep.date}
            onDateChange={(value) => setWorksiteRep((p) => ({ ...p, date: value }))}
            placeholder="Type signature"
            inputClassName={inputClass}
          />
        </div>
      </div>

    </form>
  );
}

export function SafetyFormContent({ formIndex, projectName, tabs }) {
  switch (formIndex) {
    case 0:
      return <SiteWorkerOrientationForm projectName={projectName} />;
    case 1:
      return <ExcavationInspectionForm projectName={projectName} />;
    case 2:
      return <FireExtinguisherInspectionLogForm />;
    case 3:
      return <JlgPreUseInspectionForm />;
    case 4:
      return <OfficeShopInspectionForm />;
    case 5:
      return <PreJobHazardAssessmentForm projectName={projectName} />;
    case 6:
      return <PrimeContractorChecklistForm />;
    case 7:
      return <WeeklyInspectionForm projectName={projectName} />;
    case 8:
      return <FirstAidRecordForm projectName={projectName} />;
    case 9:
      return <FieldLevelHazardAssessmentForm projectName={projectName} />;
    case 10:
      return <EcpSilicaForm projectName={projectName} />;
    case 11:
      return <FallProtectionPlanForm />;
    case 12:
      return <HsAcknowledgementForm />;
    case 13:
      return <SwpSignoffForm />;
    case 14:
      return <ConfinedSpaceEntryPlanForm />;
    default:
      return (
        <p className="text-gray-400 text-sm">
          This area is intentionally left blank for now. You can add the detailed form or content
          for <span className="font-semibold">{tabs?.[formIndex] || 'this form'}</span> here in the
          future.
        </p>
      );
  }
}

export default UserSafetyDashboard;

